import React, { useMemo, useEffect, useState } from 'react';
import { Form, Popconfirm, InputNumber } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { indexBy } from 'utils/utils2';
import DataGrid, { DataGridColDef } from 'components/DataGrid/DataGrid';
import { useFormList } from 'hooks/useFormList';
import DeleteButton from 'components/DeleteButton';
import ButtonOperation from 'components/ButtonOperation';
import InputCurrency from 'components/InputCurrency';
import {
  TPAuthorizationDetailRulesResponse,
} from 'services/thirdParty';
import { toCurrency, toRate } from 'utils/utils';
import InputRate from 'components/InputRate';
import _, { isNil } from 'lodash';
import Modal from 'antd/lib/modal/Modal';
import GetLabel from 'DisplayFramework/components/GetLabel';
import TpRelationSelect from 'components/TpRelationSelect';

type TpAuthorizationDetailRulesProps = {
  authdetailid: number;
  currcode: string;
  onSave?: (TPAuthorizationDetailRules: any) => void;
  onCancel: () => void;
  visible: boolean;
  data?: any[];
  tpid?: number;
  hideAction?: boolean;
};

const TpAuthorizationDetailRules = (props: TpAuthorizationDetailRulesProps) => {
  const { authdetailid, currcode, visible, onCancel, onSave, data, tpid,hideAction } =
    props;
  const [form] = Form.useForm();
  const fieldName = 'tpauthrules';

  const [editingKeys, setEditingKeys] = useState<number | null>();
  /*const isEditable = (authdetailid: number): boolean =>
    editingKeys === authdetailid;
*/
  const { add, remove } = useFormList(form, fieldName, {
    idKey: 'rulid',
    addAsFirst: true,
  });

  useEffect(() => {
    if (data) {
      form.setFieldsValue({
        [fieldName]: data,
      });
    } else {
      form.setFieldsValue({
        [fieldName]: [],
      });
    }
  }, [form, data]);

  //   const handleAddRules = () => {
  //   };

  const addRow = () => {
    const row: TPAuthorizationDetailRulesResponse = {
      rulid: Math.round(_.random(10000)),
      authdetailid: authdetailid ?? Math.round(_.random(10000)),
      tpidbeneficiary: '' as any,
      authmonth: 0,
      authamount: 0,
      authtolerance: 0,
      beneficiaryname: '',
      beneficiaryreference: '',
    };
    add(row);
    setEditingKeys(-1);
  };


  const columns = useMemo<DataGridColDef<TPAuthorizationDetailRulesResponse>[]>(
    () => [
      {
        dataIndex: 'tpidbeneficiary',
        fixed: 'left',
        render: (value, record) => {
          return !isNil(authdetailid) ? (
            record.beneficiaryreference + ' - ' + record.beneficiaryname
          ) : (
            <Form.Item
              noStyle
              name={[
                fieldName,
                indexBy(form.getFieldValue(fieldName), 'rulid')[record.rulid],
                'tpidbeneficiary',
              ]}
            >
              <TpRelationSelect tpid={tpid ?? 0} />
            </Form.Item>
          );
        },
        width: 150,
      },
      {
        dataIndex: 'authmonth',
        render: (value, record) => {
          return !isNil(authdetailid) ? (
            record.authmonth
          ) : (
            <Form.Item
              noStyle
              name={[
                fieldName,
                indexBy(form.getFieldValue(fieldName), 'rulid')[record.rulid],
                'authmonth',
              ]}
            >
              <InputNumber />
            </Form.Item>
          );
        },
        width: 80,
      },
      {
        dataIndex: 'authamount',
        render: (value, record) => {
          return !isNil(authdetailid) ? (
            toCurrency(
              value,
              currcode ?? localStorage.getItem('currcode') ?? ''
            )
          ) : (
            <Form.Item
              noStyle
              name={[
                fieldName,
                indexBy(form.getFieldValue(fieldName), 'rulid')[record.rulid],
                'authamount',
              ]}
            >
              <InputCurrency
                currcode={localStorage.getItem('currcode') ?? ''}
              />
            </Form.Item>
          );
        },
        width: 100,
      },
      {
        dataIndex: 'authtolerance',
        render: (value, record) => {
          return !isNil(authdetailid) ? (
            toRate(value)
          ) : (
            <Form.Item
              noStyle
              name={[
                fieldName,
                indexBy(form.getFieldValue(fieldName), 'rulid')[record.rulid],
                'authtolerance',
              ]}
            >
              <InputRate />
            </Form.Item>
          );
        },
        width: 130,
      },
      {
        key: 'actions',
        render: (_, _record) => (
          <Popconfirm
            title="Sure to delete?"
            onConfirm={() => {
              remove(_record.rulid);
            }}
          >
            <DeleteButton onlyIcon />
          </Popconfirm>
        ),

        width: hideAction ? 'hide': 80 ,
        fixed: 'right',
      },
    ],
    [form, remove, editingKeys]
  );

  return (
    <Modal
      visible={visible}
      title={<GetLabel label="Rules" />}
      footer={
        !isNil(authdetailid)
          ? null
          : [
              <ButtonOperation
                key="Cancel"
                type="default"
                onClick={() => {
                  onCancel();
                }}
                comcode="Cancel"
              />,
              <ButtonOperation
                key="Validate"
                type="primary"
                onClick={() => {

                  let data = form.getFieldsValue();
                  let tpauthrulesList: any[] = data.tpauthrules;

                  onSave && onSave(tpauthrulesList);

                  onCancel();
                }}
                comcode="Validate"
              />,
            ]
      }
      onCancel={() => onCancel()}
      width="850px"
      style={{
        top: 20,
        right: 0,
      }}
      destroyOnClose
      onOk={() => onSave}
    >
      <Form form={form}>
        <Form.Item noStyle shouldUpdate>
          {({ getFieldValue }) => {
            const prefinstep: TPAuthorizationDetailRulesResponse[] =
              getFieldValue(fieldName);
            return (
              <DataGrid
                rowKey="rulid"
                columns={columns}
                dataSource={prefinstep}
                titleComcode="tpauthrules"
                hideFilters
                hideExtraAction={!isNil(authdetailid)}
                extraActions={
                  <ButtonOperation
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={addRow}
                    comcode="add"
                  />
                }
              />
            );
          }}
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default TpAuthorizationDetailRules;
